import { FC, MouseEventHandler, useState } from "react";
import { useTranslation } from "react-i18next";
import svgIcons from "../../../assets/svg/svgIcons";
import useBuzzerAlertActions from "../../../hooks/buzzerAlertActions";
import {
  BuzzerAlert,
  BuzzerAlertStatuses
} from "../../../api/types/Notifications";
import { getBuzzerAlertUUID } from "../../../utils/notification";

type TBuzzerAlertStatusDropdownProps = {
  buzzerAlert: BuzzerAlert;
  editPermission?: boolean;
};

const BuzzerAlertStatusDropdown: FC<TBuzzerAlertStatusDropdownProps> = ({
  buzzerAlert,
  editPermission = false
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "buzzerAlerts.actions"
  });
  const [showDropdown, setShowDropdown] = useState(false);

  const buttonClass =
    "block text-left rounded-lg px-4 py-2 text-xs cursor-pointer hover:text-white";

  const { updateBuzzerAlert } = useBuzzerAlertActions();
  const [isLoading, setIsLoading] = useState(false);

  const handleUpdateBuzzerAlertStatus: (
    e: React.MouseEvent<HTMLButtonElement>,
    status: BuzzerAlertStatuses
  ) => void = (e, status) => {
    e.stopPropagation();
    updateBuzzerAlert(
      buzzerAlert.id,
      getBuzzerAlertUUID(buzzerAlert),
      status,
      setIsLoading,
      () => {
        showDropdown && setShowDropdown(false);
      }
    );
  };

  return (
    <>
      {showDropdown && (
        <div
          className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 z-overlay"
          onClick={(e) => {
            e.stopPropagation();
            setShowDropdown(false);
          }}
        ></div>
      )}
      <span className={`relative${showDropdown ? " z-modal" : ""}`}>
        <button
          className={`px-2 flex ${
            buzzerAlert.status === BuzzerAlertStatuses.RESOLVED
              ? "text-field-success"
              : "text-field-error-dark"
          }`}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowDropdown(!showDropdown);
          }}
          disabled={isLoading || !editPermission}
        >
          {t(
            `label_${
              buzzerAlert.status === BuzzerAlertStatuses.PENDING
                ? "pending"
                : buzzerAlert.status === BuzzerAlertStatuses.INPROGRESS
                  ? "inprogress"
                  : "resolved"
            }`
          )}
          {editPermission && (
            <img
              src={svgIcons.ChevronRight}
              alt="dropdown"
              className="size-6 ml-1 rotate-90"
            />
          )}
        </button>

        {editPermission && (
          <div
            className={`absolute top-1/2 left-1/2 -translate-x-3/4 -translate-y-1/2 z-10 divide-y divide-gray-100 rounded-md border border-gray-100 bg-white shadow-lg z-modal${showDropdown ? "" : " hidden"}`}
            role="menu"
          >
            <div className="flex gap-1 p-1">
              {buzzerAlert.status === BuzzerAlertStatuses.PENDING && (
                <button
                  type="button"
                  className={`${buttonClass} bg-field-intermediate/10 text-field-intermediate hover:bg-field-intermediate/80`}
                  role="menuitem"
                  onClick={(e) =>
                    handleUpdateBuzzerAlertStatus(
                      e,
                      BuzzerAlertStatuses.INPROGRESS
                    )
                  }
                  disabled={isLoading}
                >
                  {t("in_progress")}
                </button>
              )}
              <button
                type="button"
                className={`${buttonClass} bg-field-success/10 text-field-success hover:bg-field-success/80`}
                role="menuitem"
                onClick={(e) =>
                  handleUpdateBuzzerAlertStatus(e, BuzzerAlertStatuses.RESOLVED)
                }
                disabled={isLoading}
              >
                {t("resolved")}
              </button>
              <button
                type="button"
                className={`${buttonClass} text-gray-400 bg-gray-100 hover:bg-gray-400`}
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation();
                  showDropdown && setShowDropdown(false);
                }}
                disabled={isLoading}
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        )}
      </span>
    </>
  );
};
export default BuzzerAlertStatusDropdown;
